import { onAuthStateChanged } from "firebase/auth";
import { get, ref } from "firebase/database";
import { useEffect, useState } from "react";
import { auth, db, Firebase } from "@/firebase/firebase";
import { useStore } from "@/store";
import { useActiveUser, useDialog } from "@/store/selectors";
import { RealtimeDatabase } from "@/services/RealtimeDatabase";
import { StoreService } from "@/services/StoreService";
import { UserProfileService } from "@/services/UserProfileService";
import { useIsPartnerApp } from "@/store/selectors/featureSelectors";
import { config } from "@/config";
import { AssignmentService } from "@/services/AssignmentService";
import { Analytics } from "@/services/Analytics";
import { encodeEmailForRealtimeDbKey } from "@/lib/helpers";

let didShowCookieDialog = false;
export function Auth() {
  const setUser = useStore((state) => state.user.setUser);
  const { closeActiveDialog } = useDialog();
  const updateActiveUser = useStore((state) => state.user.updateActiveUser);
  const activeUser = useActiveUser();
  const { activeDialogId, openDialog: openCookieDialog } =
    useDialog("cookieConsent");
  const [didSubscribeToUser, setDidSubscribeToUser] = useState(false);
  const clearNumVideosPlayed = useStore(
    (state) => state.user.clearNumVideosPlayed,
  );
  const clearFullActiveAssignment = useStore(
    (state) => state.partnerApp.clearFullActiveAssignment,
  );
  const isPartnerApp = useIsPartnerApp();

  useEffect(() => {
    if (config.isDemoApp && !activeUser) {
      Firebase.signInAnon();
    }
  }, []);

  useEffect(() => {
    onAuthStateChanged(auth, (user) => {
      if (user) {
        setUser(user);
        clearNumVideosPlayed();
        setDidSubscribeToUser(false);

        // try {
        //   mixpanel.identify(user.uid);
        //   mixpanel.people.set({
        //     $name: user.displayName || user.email?.split("@")[0] || "Anonymous",
        //     $email: user.email,
        //     isAnonymous: user.isAnonymous,
        //   });
        // } catch (error) {
        //   console.warn("Error identifying and setting Mixpanel user", error);
        // }
      } else {
        setUser(null);
        closeActiveDialog();
        clearFullActiveAssignment();
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!activeUser || didSubscribeToUser) return;

    setDidSubscribeToUser(true);

    // Subscribe to user data
    RealtimeDatabase.subscribeToUser(activeUser.id, (userData: any) => {
      const copy = { ...userData };
      userData = userData || {};
      const keysToExclude = ["profileData", "playlistData", "advizerData"];

      console.log("Subscribe to user data", userData);

      keysToExclude.forEach((key) => {
        delete copy[key];
      });

      updateActiveUser({
        ...copy,
        acceptedCookies: userData?.acceptedCookies || false,
        rejectedCookies: userData?.rejectedCookies || false,
      });

      const playlistData = userData?.playlistData || {};
      const userProfileData = userData?.profileData || {};
      const advizerData = userData?.advizerData || {};
      // const partnerData = userData?.partnerData || {};

      if (!userData?.playlistData && !userData?.profileData) {
        UserProfileService.updateUserProfileAndPlaylistDataFromStore();
      }

      if (isPartnerApp && !userData?.partnerData) {
        RealtimeDatabase.updateCurrentUser({
          partnerData: {
            isPartnerUser: true,
            partnerId: userData?.partnerId || null,
            partnerSlug: userData?.partnerSlug || null,
          },
        });
      }

      StoreService.createStateUpdater(
        (draft) => {
          if (draft.user) {
            draft.user.playlistData = playlistData;
          }
        },
        { saveLocal: false },
      );

      StoreService.createStateUpdater(
        (draft) => {
          if (draft.user) {
            draft.user.profileData = userProfileData || {};
          }
        },
        { saveLocal: false },
      );

      StoreService.createStateUpdater(
        (draft) => {
          if (draft.user) {
            draft.user.advizerData = advizerData;
          }
        },
        { saveLocal: false },
      );

      StoreService.setDidLoadUserData(true);

      if (
        !userData?.acceptedCookies &&
        !userData?.rejectedCookies &&
        !activeDialogId &&
        !config.isDemoApp
      ) {
        console.log("showing cookie dialog", {
          didShowCookieDialog,
          userData,
          activeDialogId,
        });
        if (!didShowCookieDialog) {
          didShowCookieDialog = true;
          setTimeout(() => {
            openCookieDialog();
          }, 3000);
        }
      }

      if (activeUser?.email) {
        const normalizedEmail = activeUser.email.toLowerCase().trim();
        const emailEncoded = encodeEmailForRealtimeDbKey(normalizedEmail);
        const userUpdatePayload: Record<string, string> = {};

        if (!userData?.email) {
          userUpdatePayload.email = normalizedEmail;
        }

        if (userData?.emailEncoded !== emailEncoded) {
          userUpdatePayload.emailEncoded = emailEncoded;
        }

        if (Object.keys(userUpdatePayload).length > 0) {
          RealtimeDatabase.updateCurrentUser(userUpdatePayload);
        }
      }

      const syncStandaloneSeatAccess = async () => {
        const normalizedEmail = activeUser?.email?.toLowerCase().trim();
        const hasPartnerAssociation = Boolean(
          userData?.partnerData?.partnerId || userData?.partnerId,
        );

        console.log("syncStandaloneSeatAccess", {
          normalizedEmail,
          hasPartnerAssociation,
        });

        if (!normalizedEmail || hasPartnerAssociation) {
          updateActiveUser({
            didCheckStandaloneSeatAccess: true,
            isStandaloneSeatAccessLocked: false,
          });
          return;
        }

        try {
          const emailEncoded = encodeEmailForRealtimeDbKey(normalizedEmail);
          const seatSnapshot = await get(ref(db, `seats/emails/${emailEncoded}`));
          updateActiveUser({
            didCheckStandaloneSeatAccess: true,
            isStandaloneSeatAccessLocked: !seatSnapshot.exists(),
          });
        } catch (error) {
          console.error("Error checking standalone seat access", error);
          updateActiveUser({
            didCheckStandaloneSeatAccess: true,
            isStandaloneSeatAccessLocked: true,
          });
        }
      };

      void syncStandaloneSeatAccess();

      if (userData?.acceptedCookies) {
        Analytics.identifyUser(activeUser);
      } else {
        Analytics.setAnonymousMode();
      }
    });

    // Subscribe to user video data

    RealtimeDatabase.subscribeToUserVideoData(
      activeUser.id,
      (videoData: any) => {
        StoreService.createStateUpdater((draft) => {
          if (draft.user) {
            draft.user.videoData = videoData;
          }
        });
      },
    );

    RealtimeDatabase.subscribeToUserVideoReactions(
      activeUser.id,
      (videoReactions: any) => {
        StoreService.createStateUpdater((draft) => {
          if (draft.user) {
            draft.user.videoReactions = videoReactions;
          }
        });
      },
    );

    // Subscribe to user assignments
    AssignmentService.subscribeToUserAssignments((assignments: any) => {
      Object.keys(assignments).forEach((assignmentId) => {
        AssignmentService.subscribeToFullAssignment(
          assignmentId,
          (assignment) => {
            const assignmentCopy = { ...assignment };

            StoreService.createStateUpdater((draft) => {
              if (draft.userAssignments) {
                if (!draft.userAssignments.userAssignments) {
                  draft.userAssignments.userAssignments = {};
                }

                draft.userAssignments.userAssignments[assignmentId] = {
                  ...assignments[assignmentId],
                  ...assignmentCopy,
                };
              }
            });
          },
        );
      });
    });

    // Cleanup function - turn off all subscriptions for this user
    return () => {
      // unsubscribeFromUser?.();
      // unsubscribeFromUserVideoData?.();
      // unsubscribeFromUserAssignments?.();
      // console.log("Cleaning up subscriptions for user:", activeUser.id);
      // // Turn off user subscription
      // const userRef = ref(db, `users/${activeUser.id}`);
      // off(userRef, "value");
      // // Turn off video data subscription
      // const videoDataRef = ref(db, `videoDataByUser/${activeUser.id}`);
      // off(videoDataRef, "value");
      // // Turn off user assignments subscription
      // const assignmentsRef = ref(db, `userAssignments/${activeUser.id}`);
      // off(assignmentsRef, "value");
      // Note: Individual assignment subscriptions will need to be handled
      // if you want to clean those up too
    };
  }, [activeUser, didSubscribeToUser]);

  return null;
}
